/**
 * Shared leave-report row filtering and date-range logic.
 * `filterLeaveReportEntries` is used by LeaveReportView (table/charts) and Organisation Leave export
 * so on-screen filters and Excel export cannot drift.
 */

/** Minimum fields required for leave report search, department, leave-type, and date filters. */
export interface LeaveReportFilterableEntry {
  employeeName: string;
  businessUnitName?: string;
  departmentName?: string;
  leaveTypeName: string;
  daysTaken: number;
  fromDate?: string | null;
  toDate?: string | null;
  periodSortKey?: string | null;
}

/** Filters applied in LeaveReportView and Organisation Leave export (same order). */
export interface LeaveReportEntryFilters {
  searchQuery?: string;
  leaveTypeFilterName?: string;
  departmentFilterId?: string;
  departments?: { id: string; name: string }[];
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Filter leave report rows for display (charts/table) and as the input to export.
 * Organisation Leave applies country filtering upstream before calling this.
 */
export function filterLeaveReportEntries<T extends LeaveReportFilterableEntry>(
  entries: T[],
  filters: LeaveReportEntryFilters
): T[] {
  let rows = entries;
  const searchQuery = filters.searchQuery?.trim() ?? '';
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    rows = rows.filter(
      (e) =>
        (e.employeeName ?? '').toLowerCase().includes(q) ||
        (e.businessUnitName ?? '').toLowerCase().includes(q)
    );
  }
  if (filters.leaveTypeFilterName) {
    rows = rows.filter((e) => e.leaveTypeName === filters.leaveTypeFilterName);
  }
  const departmentFilterId = filters.departmentFilterId ?? '';
  const departments = filters.departments ?? [];
  if (departmentFilterId && departments.length > 0) {
    const deptName = departments.find((d) => d.id === departmentFilterId)?.name;
    if (deptName) {
      rows = rows.filter((e) => (e.departmentName ?? '') === deptName);
    }
  }
  const dateFrom = filters.dateFrom;
  const dateTo = filters.dateTo;
  if (dateFrom && dateTo) {
    rows = rows.filter((e) => entryOverlapsDateRange(e, dateFrom, dateTo));
  }
  return rows;
}

/** Map filtered rows to export shape with resolved From/To columns (drops rows with no resolvable period). */
export function prepareLeaveReportExportRows<T extends LeaveReportFilterableEntry>(
  entries: T[]
): (T & { fromDate: string; toDate: string })[] {
  return entries.flatMap((row) => {
    const range = resolveReportEntryDateRange(row);
    if (!range) return [];
    return [{ ...row, fromDate: range.fromDate, toDate: range.toDate }];
  });
}

/**
 * Last calendar day of the month for a yyyy-MM-dd (first-of-month) string, as yyyy-MM-dd.
 * Uses UTC date math only so overlap checks match on every host timezone (CI is often UTC).
 */
function calendarMonthEndIso(firstOfMonthIso: string): string {
  const key = firstOfMonthIso.slice(0, 10);
  const parts = key.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return '';
  const y = parts[0];
  const m = parts[1];
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

/** Resolved calendar dates for export (approved range or imported month). */
export function resolveReportEntryDateRange(entry: {
  fromDate?: string | null;
  toDate?: string | null;
  periodSortKey?: string | null;
}): { fromDate: string; toDate: string } | null {
  if (entry.fromDate && entry.toDate) {
    return {
      fromDate: entry.fromDate.slice(0, 10),
      toDate: entry.toDate.slice(0, 10),
    };
  }
  if (entry.periodSortKey) {
    const fromDate = entry.periodSortKey.slice(0, 10);
    const toDate = calendarMonthEndIso(fromDate);
    if (!toDate) return null;
    return { fromDate, toDate };
  }
  return null;
}

/**
 * Whether a leave report row overlaps a date range (inclusive). Uses date-only
 * comparison for approved ranges; imported months use periodSortKey month.
 */
export function entryOverlapsDateRange(
  entry: {
    fromDate?: string | null;
    toDate?: string | null;
    periodSortKey?: string | null;
  },
  rangeStart: string,
  rangeEnd: string
): boolean {
  const rs = rangeStart.slice(0, 10);
  const re = rangeEnd.slice(0, 10);
  if (entry.fromDate && entry.toDate) {
    const from = entry.fromDate.slice(0, 10);
    const to = entry.toDate.slice(0, 10);
    return to >= rs && from <= re;
  }
  if (entry.periodSortKey) {
    const key = entry.periodSortKey.slice(0, 10);
    const monthEnd = calendarMonthEndIso(key);
    if (!monthEnd) return true;
    return monthEnd >= rs && key <= re;
  }
  return true;
}
